'use client';

import { Ref, useLayoutEffect, useRef, useState } from 'react';
import {
  useCalendarEvents,
  CalendarEvent,
} from '../../hooks/useCalendarEvents';
import stamp from '../../public/stickers/stamp.png';
import Image from 'next/image';
import { MONTH_NAMES, parseDate } from '../../utils/utils';

function groupByMonth(events: CalendarEvent[]) {
  const groups: { key: string; month: string; events: CalendarEvent[] }[] = [];
  for (const event of events) {
    const key = event.date.slice(0, 7); // "YYYY-MM"
    let group = groups.find((g) => g.key === key);
    if (!group) {
      const month = MONTH_NAMES[parseDate(event.date).getMonth()];
      group = { key, month, events: [] };
      groups.push(group);
    }
    group.events.push(event);
  }
  return groups;
}

const SocialCalendar = () => {
  const { events, loading, error } = useCalendarEvents();
  const groups = groupByMonth(events);
  const isEmpty = !loading && !error && groups.length === 0;

  const scrollRef = useRef<HTMLDivElement>(null);
  const firstUpcomingRef = useRef<HTMLElement>(null);
  const firstUpcomingId = events.find((event) => !event.isPast)?.id;
  // The scrollbar is hidden, so a sliver of the card behind the opening one -
  // under a fade, so it reads as "there's more back there" rather than as a
  // clipped card - is the only cue that the strip starts part-way in.
  const [canScrollBack, setCanScrollBack] = useState(false);

  // The strip holds past events to the left of the upcoming ones, so it has
  // to start scrolled past them - otherwise the first thing a visitor sees
  // is history. A layout effect so the jump lands before paint, and a plain
  // scrollLeft write so it isn't animated into something that reads like a
  // scroll the visitor made.
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const target = firstUpcomingRef.current;
    if (!target) {
      // Nothing upcoming at all: the most recent event is the far end.
      container.scrollLeft = container.scrollWidth;
    } else {
      // Stop --peek short of the card so the tail of the one before it stays
      // in view, with the row's normal gap between them. Both values are read
      // from the DOM so the fade overlay, which is sized by the same custom
      // property, can't drift out of step with the scroll position.
      const row = container.firstElementChild;
      const style = row ? window.getComputedStyle(row) : null;
      const peek = style
        ? parseFloat(style.getPropertyValue('--peek')) || 0
        : 0;
      const gap = style ? parseFloat(style.columnGap) || 0 : 0;

      container.scrollLeft +=
        target.getBoundingClientRect().left -
        container.getBoundingClientRect().left -
        peek -
        gap;
    }

    // Read back rather than reuse the value written above - the browser
    // clamps it, so a strip with nothing to its left stays at 0.
    setCanScrollBack(container.scrollLeft > 0);
  }, [events]);

  // Kept mounted across every state (loading/empty/content) so its text
  // change is what gets announced - removing a live region instead of
  // updating it isn't picked up by screen readers.
  const statusMessage = loading
    ? 'Laster kommende arrangementer …'
    : isEmpty
      ? 'Ingen kommende arrangementer'
      : '';

  return (
    <>
      <span className="sr-only" role="status" aria-live="polite">
        {statusMessage}
      </span>
      {loading ? (
        <CalendarSkeleton />
      ) : error || isEmpty ? null : (
        <section aria-label="Dette skjer i Blank" className="w-full self-start">
          <div className="w-full">
            <h2 className="font-respira text-respira-sm tablet:text-respira-md mb-16 tablet:pl-12">
              Dette skjer i Blank
            </h2>
            <div className="relative -mr-16 laptop:-mr-24 [--peek:40px] tablet:[--peek:64px]">
              {/* relative is load-bearing: the cards' sr-only text is
                  position:absolute, and an absolutely positioned box whose
                  containing block sits outside a scroll container is neither
                  clipped by it nor moved by its scroll - so without a
                  positioned scroller those spans park at their un-scrolled
                  offsets, thousands of pixels to the right, and drag the whole
                  page into horizontal scroll. */}
              <div
                ref={scrollRef}
                onScroll={(scroll) =>
                  setCanScrollBack(scroll.currentTarget.scrollLeft > 0)
                }
                className="relative overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              >
                <div className="flex items-start gap-6 tablet:pl-12 pr-12 w-full">
                  {groups.map((group) => (
                    <div key={group.key} className="flex flex-col gap-16">
                      {/* Pinned to the row's own gutter so it stays put while
                          its month scrolls past, and only leaves when the next
                          month pushes it out. w-fit because a stretched
                          heading fills the column and so has no room to slide;
                          z-10 to sit above the edge fade, which would
                          otherwise wash out its first few letters. */}
                      <h3 className="sticky left-0 tablet:left-12 z-10 w-fit text-xl tablet:text-2xl">
                        {group.month}
                      </h3>
                      <div className="flex flex-row gap-6" role="list">
                        {group.events.map((event) => (
                          <Happening
                            key={event.id}
                            ref={
                              event.id === firstUpcomingId
                                ? firstUpcomingRef
                                : undefined
                            }
                            title={event.title}
                            date={event.date}
                            imageUrl={event.imageUrl}
                            isPast={event.isPast}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div
                aria-hidden="true"
                className={`pointer-events-none absolute inset-y-0 left-0 w-(--peek) bg-linear-to-r from-blank-black to-transparent transition-opacity duration-200 ${
                  canScrollBack ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </div>
          </div>
        </section>
      )}
    </>
  );
};

const Happening = ({
  title,
  date,
  imageUrl,
  isPast,
  ref,
}: Pick<CalendarEvent, 'title' | 'date' | 'imageUrl' | 'isPast'> & {
  ref?: Ref<HTMLElement>;
}) => {
  const [imageError, setImageError] = useState(false);
  const showImage = imageUrl && !imageError;

  const formattedDate = parseDate(date).toLocaleDateString('nb-NO', {
    day: 'numeric',
    month: 'long',
  });

  return (
    <article
      ref={ref}
      role="listitem"
      className={`flex flex-col gap-3 w-128 tablet:w-[229px] shrink-0 ${
        isPast ? 'opacity-50' : ''
      }`}
    >
      <div className="relative w-full aspect-square overflow-hidden bg-blank-yellow">
        {showImage ? (
          <Image
            src={imageUrl}
            alt={`Bilde fra ${title}`}
            fill
            unoptimized
            className="object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div
            className="w-full h-full bg-blank-yellow flex items-center justify-center p-8"
            aria-hidden="true"
          >
            <Image src={stamp} alt="" className="w-[80%] object-contain" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <h4 className="text-xl tablet:text-2xl hyphens-auto wrap-break-words">
          {isPast && <span className="sr-only">Tidligere: </span>}
          {title}
        </h4>
        <time className="text-lg tablet:text-xl" dateTime={date}>
          {formattedDate}
        </time>
      </div>
    </article>
  );
};

// Sized by the same text-size utility as the real element it stands in for,
// so its line box (and responsive growth at tablet:) always matches, instead
// of a hand-picked height that can drift out of sync with the type scale.
const TextSkeleton = ({ className }: { className: string }) => (
  <div
    className={`inline-block rounded bg-blank-yellow/20 text-transparent animate-pulse ${className}`}
  >
    &nbsp;
  </div>
);

const CalendarSkeleton = () => (
  <div className="w-full self-start" aria-hidden="true">
    <div className="mb-16 tablet:pl-12">
      <TextSkeleton className="font-respira text-respira-sm tablet:text-respira-md w-192" />
    </div>
    <div className="overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] -mr-16 laptop:-mr-24">
      <div className="flex items-start gap-6 tablet:pl-12 pr-12 w-full">
        {/* A single month: a second heading part-way along would promise a
            month boundary at a spot the real strip almost never has one. */}
        <div className="flex flex-col gap-16">
          <TextSkeleton className="text-xl tablet:text-2xl w-96" />
          <div className="flex flex-row gap-6">
            {[1, 2, 3, 4, 5, 6].map((card) => (
              <div
                key={card}
                className="flex flex-col gap-3 w-128 tablet:w-[229px] shrink-0"
              >
                <div className="w-full aspect-square bg-blank-yellow/20 animate-pulse" />
                <div className="flex flex-col gap-1">
                  <TextSkeleton className="text-xl tablet:text-2xl w-3/4" />
                  <TextSkeleton className="text-lg tablet:text-xl w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export { SocialCalendar };
